import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import JsonKeyValueEditor from './JsonKeyValueEditor';

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => Promise<void>;
  submitting?: boolean;
  initialValue?: any | null;
};

const MASKED_SECRET_VALUE = '*********************************';
const REDACTED_VALUES = new Set(['[stored securely]', '[redacted]']);

const emptyForm = {
  id: undefined,
  name: '',
  slug: '',
  base_url: '',
  server_type: 'internal',
  transport_type: 'streamable_http',
  auth_type: 'none',
  enabled: true,
  is_default: false,
  priority: 100,
  timeout_seconds: 30,
  verify_ssl: true,
  healthcheck_path: '/health',
  mcp_path_prefix: '/mcp',
  description: '',
  auth_configs: [],
  capabilities: [],
};

function isMaskedSecret(value: any) {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  return trimmed === MASKED_SECRET_VALUE || /^\*+$/.test(trimmed);
}

function cleanCredentialForForm(credential?: Record<string, any>) {
  const next = { ...(credential || {}) };

  Object.keys(next).forEach((key) => {
    if (REDACTED_VALUES.has(String(next[key] || '').trim())) {
      next[key] = MASKED_SECRET_VALUE;
    }
  });

  return next;
}

function normalizeAuthConfigsForSubmit(rows: any[]) {
  return (rows || []).map((row) => {
    const authType = row?.auth_type || 'none';
    const credential = { ...(row?.credential || {}) };

    if (authType === 'bearer') {
      if (isMaskedSecret(credential.value)) delete credential.value;
      return { ...row, credential };
    }

    if (authType === 'oauth_client_credentials') {
      if (isMaskedSecret(credential.client_secret)) delete credential.client_secret;
      return { ...row, credential };
    }

    if (authType === 'api_key') {
      const entries = Object.entries(credential);
      const [headerKey, apiKey] = entries[0] || ['', ''];

      if (!headerKey || isMaskedSecret(apiKey)) {
        return { ...row, credential: {} };
      }

      return {
        ...row,
        credential: {
          [String(headerKey)]: apiKey,
        },
      };
    }

    return { ...row, credential };
  });
}

function getApiKeyCredentialEntry(credential?: Record<string, any>) {
  const entries = Object.entries(credential || {});
  const [firstKey, firstValue] = entries[0] || ['', ''];
  return {
    headerKey: firstKey,
    apiKey: typeof firstValue === 'string' ? firstValue : '',
  };
}

const MCPServerEditorDialog: React.FC<Props> = ({
                                                  open,
                                                  onClose,
                                                  onSubmit,
                                                  submitting,
                                                  initialValue,
                                                }) => {
  const [tab, setTab] = React.useState(0);
  const [form, setForm] = React.useState<any>(emptyForm);

  React.useEffect(() => {
    if (!open) return;

    const authConfigs = Array.isArray(initialValue?.auth_configs)
      ? initialValue.auth_configs.map((auth: any) => ({
        ...auth,
        credential: cleanCredentialForForm(auth?.credential),
      }))
      : [];

    setForm({
      ...emptyForm,
      ...(initialValue || {}),
      auth_configs: authConfigs,
      capabilities: Array.isArray(initialValue?.capabilities)
        ? initialValue.capabilities
        : [],
    });
    setTab(0);
  }, [initialValue, open]);

  const setField = (key: string, value: any) => {
    setForm((prev: any) => ({ ...prev, [key]: value }));
  };

  const addAuth = () => {
    setForm((prev: any) => ({
      ...prev,
      auth_configs: [
        ...(prev.auth_configs || []),
        {
          auth_type: 'none',
          credential: {},
        },
      ],
    }));
  };

  const updateAuth = (idx: number, patch: any) => {
    setForm((prev: any) => {
      const next = [...(prev.auth_configs || [])];
      next[idx] = { ...next[idx], ...patch };
      return { ...prev, auth_configs: next };
    });
  };

  const removeAuth = (idx: number) => {
    setForm((prev: any) => ({
      ...prev,
      auth_configs: (prev.auth_configs || []).filter((_: any, i: number) => i !== idx),
    }));
  };

  const updateAuthCredential = (idx: number, key: string, value: any) => {
    setForm((prev: any) => {
      const next = [...(prev.auth_configs || [])];
      const current = next[idx] || {};
      next[idx] = {
        ...current,
        credential: {
          ...(current.credential || {}),
          [key]: value,
        },
      };
      return { ...prev, auth_configs: next };
    });
  };

  const updateApiKeyAuthCredential = (idx: number, headerKey: string, apiKey: string) => {
    setForm((prev: any) => {
      const next = [...(prev.auth_configs || [])];
      const current = next[idx] || {};
      next[idx] = {
        ...current,
        credential: headerKey.trim() ? { [headerKey]: apiKey } : {},
      };
      return { ...prev, auth_configs: next };
    });
  };

  const addCapability = () => {
    setForm((prev: any) => ({
      ...prev,
      capabilities: [
        ...(prev.capabilities || []),
        {
          capability_type: 'tool',
          capability_name: '',
          display_name: '',
          description: '',
          allowed: true,
          enabled: true,
          config_json: {},
        },
      ],
    }));
  };

  const updateCapability = (idx: number, key: string, value: any) => {
    setForm((prev: any) => {
      const next = [...(prev.capabilities || [])];
      next[idx] = { ...next[idx], [key]: value };
      return { ...prev, capabilities: next };
    });
  };

  const removeCapability = (idx: number) => {
    setForm((prev: any) => ({
      ...prev,
      capabilities: (prev.capabilities || []).filter((_: any, i: number) => i !== idx),
    }));
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return window.alert('Name is required.');
    if (!form.slug.trim()) return window.alert('Slug is required.');
    if (!form.base_url.trim()) return window.alert('Base URL is required.');

    await onSubmit({
      ...form,
      priority: Number(form.priority),
      timeout_seconds: Number(form.timeout_seconds),
      auth_configs: normalizeAuthConfigsForSubmit(form.auth_configs || []),
      capabilities: form.capabilities || [],
    });
  };

  const isEdit = Boolean(form?.id);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{isEdit ? 'Edit MCP Server' : 'Add MCP Server'}</DialogTitle>

      <DialogContent dividers>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ mb: 2 }}>
          <Tab label="Server" />
          <Tab label="Auth" />
          <Tab label="Capabilities" />
        </Tabs>

        {tab === 0 ? (
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={form.name}
                  onChange={(event) => setField('name', event.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Slug"
                  value={form.slug}
                  onChange={(event) => setField('slug', event.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Base URL"
                  value={form.base_url}
                  onChange={(event) => setField('base_url', event.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Server Type"
                  value={form.server_type}
                  onChange={(event) => setField('server_type', event.target.value)}
                >
                  <MenuItem value="internal">internal</MenuItem>
                  <MenuItem value="external">external</MenuItem>
                  <MenuItem value="partner">partner</MenuItem>
                  <MenuItem value="custom">custom</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Transport"
                  value={form.transport_type}
                  onChange={(event) => setField('transport_type', event.target.value)}
                >
                  <MenuItem value="http">http</MenuItem>
                  <MenuItem value="streamable_http">streamable_http</MenuItem>
                  <MenuItem value="sse">sse</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  select
                  label="Default Auth Type"
                  value={form.auth_type}
                  onChange={(event) => setField('auth_type', event.target.value)}
                >
                  <MenuItem value="none">none</MenuItem>
                  <MenuItem value="bearer">bearer</MenuItem>
                  <MenuItem value="api_key">api_key</MenuItem>
                  <MenuItem value="oauth_client_credentials">
                    oauth_client_credentials
                  </MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Priority"
                  value={form.priority}
                  onChange={(event) => setField('priority', event.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Timeout Seconds"
                  value={form.timeout_seconds}
                  onChange={(event) => setField('timeout_seconds', event.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Healthcheck Path"
                  value={form.healthcheck_path}
                  onChange={(event) => setField('healthcheck_path', event.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="MCP Path Prefix"
                  value={form.mcp_path_prefix}
                  onChange={(event) => setField('mcp_path_prefix', event.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="Description"
                  value={form.description}
                  onChange={(event) => setField('description', event.target.value)}
                />
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form.enabled}
                    onChange={(event) => setField('enabled', event.target.checked)}
                  />
                }
                label="Enabled"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form.is_default}
                    onChange={(event) => setField('is_default', event.target.checked)}
                  />
                }
                label="Default Server"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!form.verify_ssl}
                    onChange={(event) => setField('verify_ssl', event.target.checked)}
                  />
                }
                label="Verify SSL"
              />
            </Stack>
          </Stack>
        ) : null}

        {tab === 1 ? (
          <Stack spacing={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">Authentication</Typography>
              <Button variant="contained" onClick={addAuth}>
                Add Auth Config
              </Button>
            </Box>

            {(form.auth_configs || []).map((auth: any, idx: number) => {
              const authType = auth.auth_type || 'none';
              const credential = auth.credential || {};

              return (
                <Paper key={auth.id || idx} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      select
                      label="Auth Type"
                      value={authType}
                      onChange={(event) =>
                        updateAuth(idx, {
                          auth_type: event.target.value,
                          credential: {},
                        })
                      }
                    >
                      <MenuItem value="none">none</MenuItem>
                      <MenuItem value="bearer">bearer</MenuItem>
                      <MenuItem value="api_key">api_key</MenuItem>
                      <MenuItem value="oauth_client_credentials">
                        oauth_client_credentials
                      </MenuItem>
                    </TextField>

                    {authType === 'bearer' ? (
                      <TextField
                        fullWidth
                        type="password"
                        label="Bearer Token"
                        value={credential.value || ''}
                        onChange={(event) =>
                          updateAuthCredential(idx, 'value', event.target.value)
                        }
                        helperText="Leave masked value unchanged to preserve the stored secret."
                      />
                    ) : null}

                    {authType === 'api_key'
                      ? (() => {
                        const { headerKey, apiKey } =
                          getApiKeyCredentialEntry(credential);

                        return (
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Header Key"
                                placeholder="x-api-key"
                                value={headerKey}
                                onChange={(event) =>
                                  updateApiKeyAuthCredential(
                                    idx,
                                    event.target.value,
                                    apiKey
                                  )
                                }
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                type="password"
                                label="API Key"
                                value={apiKey}
                                onChange={(event) =>
                                  updateApiKeyAuthCredential(
                                    idx,
                                    headerKey,
                                    event.target.value
                                  )
                                }
                                helperText="Leave masked value unchanged to preserve the stored secret."
                              />
                            </Grid>
                          </Grid>
                        );
                      })()
                      : null}

                    {authType === 'oauth_client_credentials' ? (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Token URL"
                            value={credential.token_url || ''}
                            onChange={(event) =>
                              updateAuthCredential(idx, 'token_url', event.target.value)
                            }
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Client ID"
                            value={credential.client_id || ''}
                            onChange={(event) =>
                              updateAuthCredential(idx, 'client_id', event.target.value)
                            }
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            type="password"
                            label="Client Secret"
                            value={credential.client_secret || ''}
                            onChange={(event) =>
                              updateAuthCredential(
                                idx,
                                'client_secret',
                                event.target.value
                              )
                            }
                            helperText="Leave masked value unchanged to preserve the stored secret."
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Audience"
                            value={credential.audience || ''}
                            onChange={(event) =>
                              updateAuthCredential(idx, 'audience', event.target.value)
                            }
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            label="Scope"
                            value={credential.scope || ''}
                            onChange={(event) =>
                              updateAuthCredential(idx, 'scope', event.target.value)
                            }
                          />
                        </Grid>
                      </Grid>
                    ) : null}

                    {authType === 'none' ? (
                      <Typography variant="body2" color="text.secondary">
                        No authentication will be configured.
                      </Typography>
                    ) : null}

                    <Divider />

                    <Box>
                      <Button color="error" onClick={() => removeAuth(idx)}>
                        Remove Auth Config
                      </Button>
                    </Box>
                  </Stack>
                </Paper>
              );
            })}

            {!(form.auth_configs || []).length ? (
              <Typography variant="body2" color="text.secondary">
                No auth configs added.
              </Typography>
            ) : null}
          </Stack>
        ) : null}

        {tab === 2 ? (
          <Stack spacing={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">Configured Capabilities</Typography>
              <Button variant="contained" onClick={addCapability}>
                Add Capability
              </Button>
            </Box>

            {(form.capabilities || []).map((cap: any, idx: number) => (
              <Paper key={cap.id || idx} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        select
                        label="Capability Type"
                        value={cap.capability_type || 'tool'}
                        onChange={(event) =>
                          updateCapability(idx, 'capability_type', event.target.value)
                        }
                      >
                        <MenuItem value="tool">tool</MenuItem>
                        <MenuItem value="resource">resource</MenuItem>
                        <MenuItem value="prompt">prompt</MenuItem>
                        <MenuItem value="skill">skill</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Capability Name"
                        value={cap.capability_name || ''}
                        onChange={(event) =>
                          updateCapability(idx, 'capability_name', event.target.value)
                        }
                      />
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <TextField
                        fullWidth
                        label="Display Name"
                        value={cap.display_name || ''}
                        onChange={(event) =>
                          updateCapability(idx, 'display_name', event.target.value)
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        label="Description"
                        value={cap.description || ''}
                        onChange={(event) =>
                          updateCapability(idx, 'description', event.target.value)
                        }
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <JsonKeyValueEditor
                        label="Capability Config"
                        value={cap.config_json || {}}
                        onChange={(next) => updateCapability(idx, 'config_json', next)}
                      />
                    </Grid>
                  </Grid>

                  <Stack direction="row" spacing={2}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!cap.enabled}
                          onChange={(event) =>
                            updateCapability(idx, 'enabled', event.target.checked)
                          }
                        />
                      }
                      label="Enabled"
                    />

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={cap.allowed !== false}
                          onChange={(event) =>
                            updateCapability(idx, 'allowed', event.target.checked)
                          }
                        />
                      }
                      label="Allowed"
                    />
                  </Stack>

                  <Divider />

                  <Box>
                    <Button color="error" onClick={() => removeCapability(idx)}>
                      Remove Capability
                    </Button>
                  </Box>
                </Stack>
              </Paper>
            ))}

            {!(form.capabilities || []).length ? (
              <Typography variant="body2" color="text.secondary">
                No configured capabilities added.
              </Typography>
            ) : null}
          </Stack>
        ) : null}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>

        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MCPServerEditorDialog;